import type { NoteIndexEntry, NoteTypeDTO } from '@kartothek/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { api } from './api';

export function useCampaignId(): string {
  const { cid } = useParams();
  return cid!;
}

export function useCampaign(cid: string) {
  return useQuery({ queryKey: ['campaign', cid], queryFn: () => api.campaign(cid) });
}

export function useNoteIndex(cid: string) {
  return useQuery({ queryKey: ['notes', cid], queryFn: () => api.notes(cid), staleTime: 60_000 });
}

export function useNote(cid: string, nid: string | undefined) {
  return useQuery({
    queryKey: ['note', cid, nid],
    queryFn: () => api.note(cid, nid!),
    enabled: !!nid,
  });
}

export function useTypes(cid: string) {
  const q = useQuery({ queryKey: ['types', cid], queryFn: () => api.types(cid), staleTime: 5 * 60_000 });
  const byKey = useMemo(() => new Map<string, NoteTypeDTO>((q.data ?? []).map((t) => [t.key, t])), [q.data]);
  return { ...q, byKey };
}

/** Lädt eine Notiz vorab (Hover, Strg+K-Auswahl), damit der Wechsel sofort wirkt. */
export function usePrefetchNote(cid: string) {
  const qc = useQueryClient();
  return (nid: string) => qc.prefetchQuery({ queryKey: ['note', cid, nid], queryFn: () => api.note(cid, nid), staleTime: 30_000 });
}

/** Nach Änderungen: Index, Graph und betroffene Notizen neu laden. */
export function useInvalidateCampaign(cid: string) {
  const qc = useQueryClient();
  // Stabil halten: wird in useCallback-Abhängigkeiten verwendet
  return useCallback(() => {
    void qc.invalidateQueries({ queryKey: ['notes', cid] });
    void qc.invalidateQueries({ queryKey: ['note', cid] });
    void qc.invalidateQueries({ queryKey: ['graph', cid] });
    void qc.invalidateQueries({ queryKey: ['hierarchy', cid] });
    void qc.invalidateQueries({ queryKey: ['notesOfType', cid] });
    void qc.invalidateQueries({ queryKey: ['openLinks', cid] });
    void qc.invalidateQueries({ queryKey: ['campaign', cid] });
  }, [qc, cid]);
}

export function noteTitleMap(index: NoteIndexEntry[] | undefined) {
  const m = new Map<string, NoteIndexEntry>();
  for (const n of index ?? []) m.set(n.id, n);
  return m;
}
